import React from 'react';
import PropTypes from 'prop-types';
import LandingPageTemplate from '../../components/04_templates/PageLayouts/LandingPage';

import Paragraphs from '../../components/02_molecules/Paragraphs';
import * as field from '../../utils/transforms.fields';
import { KNOWLEDGE_HUB_URL } from '../../utils/constants';
import ResourcePageTopInfo from '../../components/03_organisms/ResourcePageTopInfo';
import { getResultCardIcon } from '../../utils/transforms.elastic_search';
import { formattedDate } from '../../utils/date-formatter';
import '../../styles/styles.scss';

const PublicationPage = ({
  entity = null,
  blocks = [],
  layout = 'twoColumns',
  resourcePageTopInfo,
  prevPath,
}) => {
  const pageBlocks = blocks.map((block) => {
    if (!block) {
      return null;
    }

    const pageBlock = block;

    // Special CW requirement - do not show type on the publications page.
    if (block.blockType === 'news_meta_info') {
      delete pageBlock.type;
    }

    return pageBlock;
  });

  const newBreadrumbsLinks = [
    {
      label: 'Knowledge Hub',
      nextLink: {
        as: '/knowledge-hub',
        href: '/node/page',
        isExternal: false,
        route: '/node/page',
        url: '/knowledge-hub',
      },
    },
    {
      label: field.getTextValue(entity, 'title'),
    },
  ];

  // Add Social Media share side bar
  pageBlocks.push({
    blockType: 'social_media_share',
    activeProviders: ['facebook', 'twitter', 'linkedin', 'link'],
    hasMobileBorders: true,
    isDesktopSticky: true,
  });

  return (
    <LandingPageTemplate>
      <ResourcePageTopInfo
        breadcrumbs={{ links: newBreadrumbsLinks }}
        showBackButton={Boolean(prevPath) && prevPath.startsWith(KNOWLEDGE_HUB_URL)}
        {...resourcePageTopInfo}
      />
      <Paragraphs blocks={pageBlocks} layout={layout} />
    </LandingPageTemplate>
  );
};

// Opt-out of Automatic Static Optimization.
// See https://nextjs.org/docs/api-reference/next.config.js/runtime-configuration
PublicationPage.getInitialProps = async ({ entity }) => {
  const initialProps = {};

  const metaInfo = [];
  let relatedCategories = [];

  // TODO: Create a new 'Knowledge Hub Resource Header' body block
  // and move all this transforms to a new ParagraphNormalizer on the backend.
  const created = field.getNumberValue(entity, 'created');
  metaInfo.push({
    title: 'Last updated',
    value: formattedDate(created),
  });

  const author = field.getArrayValue(entity, 'field_person');
  if (author.length) {
    metaInfo.push({
      title: 'Author',
      value: field.getTextValue(author[0], 'title'),
    });
  }

  const origin = field.getLinkValue(entity, 'field_link');

  if (origin && origin.nextLink) {
    metaInfo.push({
      title: 'Origin',
      value: `<a href="${origin.nextLink.url}" target="_blank" rel="noopener">${origin.label}<span class="screen-reader-only">(opens in a new tab)</span></a>`,
    });
  }

  const partners = field.getArrayValue(entity, 'field_partners_networks');
  if (partners.length) {
    metaInfo.push({
      title: 'Partner/Network',
      value: partners.map((item) => field.getTextValue(item, 'name')).join(', '),
    });
  }

  const languages = field.getArrayValue(entity, 'field_languages');
  if (languages.length) {
    metaInfo.push({
      title: 'Language',
      value: languages
        .map((item) => field.getTextValue(item, 'field_language_code').toUpperCase())
        .join(', '),
    });
  }

  const regions = field.getArrayValue(entity, 'field_regions');
  if (regions.length) {
    regions.forEach((item) =>
      relatedCategories.push({
        label: field.getTextValue(item, 'name'),
        url: `${KNOWLEDGE_HUB_URL}?filter_regions=["${field.getTextValue(item, 'name')}"]`,
      }),
    );
  }

  const countries = field.getArrayValue(entity, 'field_countries');
  if (countries.length) {
    countries.forEach((item) =>
      relatedCategories.push({
        label: item.label,
        url: `${KNOWLEDGE_HUB_URL}?filter_countries=["${item.label}"]`,
      }),
    );
  }

  const themesSectors = field.getArrayValue(entity, 'field_themes_sectors');
  if (themesSectors.length) {
    themesSectors.forEach((item) =>
      relatedCategories.push({
        label: field.getTextValue(item, 'name'),
        url: `${KNOWLEDGE_HUB_URL}?filter_themes_sectors=["${field.getTextValue(item, 'name')}"]`,
      }),
    );
  }

  const approaches = field.getArrayValue(entity, 'field_approaches');
  if (approaches.length) {
    approaches.forEach((item) =>
      relatedCategories.push({
        label: field.getTextValue(item, 'name'),
        url: `${KNOWLEDGE_HUB_URL}?filter_approaches=["${field.getTextValue(item, 'name')}"]`,
      }),
    );
  }

  const flagshipProgrammes = field.getArrayValue(entity, 'field_flagship_programmes');
  if (flagshipProgrammes.length) {
    flagshipProgrammes.forEach((item) =>
      relatedCategories.push({
        label: field.getTextValue(item, 'name'),
        url: `${KNOWLEDGE_HUB_URL}?filter_programmes=["${field.getTextValue(item, 'name')}"]`,
      }),
    );
  }

  let type = 'Publication';
  const resourceTypes = field.getArrayValue(entity, 'field_resource_types');
  if (resourceTypes.length) {
    type = field.getTextValue(resourceTypes[0], 'name');
  }

  initialProps.resourcePageTopInfo = {
    metaInfo,
    relatedCategories,
    type,
    icon: getResultCardIcon('publication', type),
    title: field.getTextValue(entity, 'title'),
  };

  return initialProps;
};

PublicationPage.propTypes = {
  entity: PropTypes.shape({
    // TODO: Define entity object shape.
  }),
  blocks: PropTypes.arrayOf(
    PropTypes.shape({
      blockType: PropTypes.string.isRequired,
    }),
  ),
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      nextLink: PropTypes.shape({
        href: PropTypes.string,
        as: PropTypes.string,
      }),
    }),
  ),
  layout: PropTypes.string,
  resourcePageTopInfo: PropTypes.shape({
    metaInfo: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        value: PropTypes.string,
      }),
    ),
    relatedCategories: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
    title: PropTypes.string.isRequired,
    type: PropTypes.string,
  }).isRequired,
  prevPath: PropTypes.string.isRequired,
};

export default PublicationPage;
